import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { formatCurrency } from '../components/helpers';

const Cell = ({ value }) => (
    <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-text-gray-700">
        {formatCurrency(value, { dashOnZero: true })}
    </td>
);

const sumGroup = (group) => {
    const sub = {
        opening_dr: 0, opening_cr: 0,
        during_year_dr: 0, during_year_cr: 0,
        closing_dr: 0, closing_cr: 0,
    };
    (group.accounts || []).forEach((acc) => {
        sub.opening_dr += parseFloat(acc.opening?.debit) || 0;
        sub.opening_cr += parseFloat(acc.opening?.credit) || 0;
        sub.during_year_dr += parseFloat(acc.duringYear?.debit) || 0;
        sub.during_year_cr += parseFloat(acc.duringYear?.credit) || 0;
        sub.closing_dr += parseFloat(acc.closing?.debit) || 0;
        sub.closing_cr += parseFloat(acc.closing?.credit) || 0;
    });
    return sub;
};

const TrialBalanceTable = ({ groups, totals, collapseAllSignal }) => {
    const [collapsed, setCollapsed] = useState({});

    const allCodes = useMemo(() => (groups || []).map((g) => g.groupCode), [groups]);

    useEffect(() => {
        if (collapseAllSignal === undefined) return;
        const anyOpen = allCodes.some((c) => !collapsed[c]);
        if (anyOpen) {
            const next = {};
            allCodes.forEach((c) => { next[c] = true; });
            setCollapsed(next);
        } else {
            setCollapsed({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collapseAllSignal]);

    const toggle = (code) => setCollapsed((prev) => ({ ...prev, [code]: !prev[code] }));

    // Compute is_balanced from totals
    const isBalanced = totals
        ? Math.abs((parseFloat(totals.closing?.debit) || 0) - (parseFloat(totals.closing?.credit) || 0)) < 0.01
        : undefined;

    if (!groups || groups.length === 0) {
        return <div className="tw-text-center tw-py-12 tw-text-xs tw-text-gray-400">No data available</div>;
    }

    return (
        <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-text-xs">
                <thead>
                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                        <th className="tw-text-left tw-px-3 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Account Name</th>
                        <th className="tw-text-left tw-px-3 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px]">Account Code</th>
                        <th colSpan={2} className="tw-text-center tw-px-3 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px] tw-border-l tw-border-gray-100">Opening Balance</th>
                        <th colSpan={2} className="tw-text-center tw-px-3 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px] tw-border-l tw-border-gray-100">During the Year</th>
                        <th colSpan={2} className="tw-text-center tw-px-3 tw-py-3 tw-font-bold tw-text-gray-700 tw-text-[11px] tw-border-l tw-border-gray-100">Closing Balance</th>
                    </tr>
                    <tr className="tw-bg-gray-50/50 tw-border-b tw-border-gray-100 tw-text-[10px] tw-text-gray-500">
                        <th></th>
                        <th></th>
                        <th className="tw-text-right tw-px-3 tw-py-1 tw-border-l tw-border-gray-100">Dr</th>
                        <th className="tw-text-right tw-px-3 tw-py-1">Cr</th>
                        <th className="tw-text-right tw-px-3 tw-py-1 tw-border-l tw-border-gray-100">Dr</th>
                        <th className="tw-text-right tw-px-3 tw-py-1">Cr</th>
                        <th className="tw-text-right tw-px-3 tw-py-1 tw-border-l tw-border-gray-100">Dr</th>
                        <th className="tw-text-right tw-px-3 tw-py-1">Cr</th>
                    </tr>
                </thead>
                <tbody>
                    {groups.map((group) => {
                        const isCollapsed = !!collapsed[group.groupCode];
                        const sub = sumGroup(group);
                        return (
                            <>
                                <tr
                                    key={group.groupCode}
                                    className="tw-bg-blue-50/40 tw-border-b tw-border-blue-100 hover:tw-bg-blue-100/40 tw-cursor-pointer"
                                    onClick={() => toggle(group.groupCode)}
                                >
                                    <td colSpan={2} className="tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-700">
                                        <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                                            <Icon
                                                icon={isCollapsed ? 'mdi:chevron-right' : 'mdi:chevron-down'}
                                                className="tw-text-gray-400 tw-text-sm"
                                            />
                                            {group.groupName} ({group.groupCode})
                                        </span>
                                    </td>
                                    <Cell value={sub.opening_dr} />
                                    <Cell value={sub.opening_cr} />
                                    <Cell value={sub.during_year_dr} />
                                    <Cell value={sub.during_year_cr} />
                                    <Cell value={sub.closing_dr} />
                                    <Cell value={sub.closing_cr} />
                                </tr>
                                {!isCollapsed && (group.accounts || []).map((acc) => (
                                    <tr key={acc.accountId ?? acc.accountCode} className="tw-border-b tw-border-gray-50 hover:tw-bg-slate-50/50">
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-700">
                                            <span className="tw-pl-5 tw-inline-block tw-text-blue-700 hover:tw-underline tw-cursor-pointer">{acc.name}</span>
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-font-mono">{acc.accountCode}</td>
                                        <Cell value={acc.opening?.debit} />
                                        <Cell value={acc.opening?.credit} />
                                        <Cell value={acc.duringYear?.debit} />
                                        <Cell value={acc.duringYear?.credit} />
                                        <Cell value={acc.closing?.debit} />
                                        <Cell value={acc.closing?.credit} />
                                    </tr>
                                ))}
                            </>
                        );
                    })}
                </tbody>
                {totals && (
                    <tfoot>
                        <tr className={`tw-bg-gray-100 tw-border-t-2 tw-border-gray-300 tw-font-bold ${isBalanced ? '' : 'tw-text-rose-600'}`}>
                            <td colSpan={2} className="tw-px-3 tw-py-3 tw-text-gray-800">
                                Grand Total
                                {isBalanced !== undefined && (
                                    <span className={`tw-ml-2 tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] ${isBalanced ? 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-rose-100 tw-text-rose-700'}`}>
                                        {isBalanced ? 'Balanced' : 'Unbalanced'}
                                    </span>
                                )}
                            </td>
                            <Cell value={totals.opening?.debit} />
                            <Cell value={totals.opening?.credit} />
                            <Cell value={totals.duringYear?.debit} />
                            <Cell value={totals.duringYear?.credit} />
                            <Cell value={totals.closing?.debit} />
                            <Cell value={totals.closing?.credit} />
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
};

export default TrialBalanceTable;
